import apiClient from "./api/client";
import ENDPOINTS from "./api/endpoints";

/**
 * Printer Configuration Service - manages printer settings
 */
const printerService = {
  /**
   * Get all active printers
   */
  getAll: async (params = {}) => {
    try {
      const response = await apiClient.get(ENDPOINTS.PRINTERS.GET_ALL, { params });
      return response?.data || [];
    } catch (error) {
      console.error("Failed to fetch printers:", error);
      throw error;
    }
  },

  /**
   * Get all printers including inactive (for admin panel)
   */
  getAllForAdmin: async () => {
    try {
      const response = await apiClient.get(ENDPOINTS.PRINTERS.GET_ALL_FOR_ADMIN);
      return response?.data || [];
    } catch (error) {
      console.error("Failed to fetch all printers for admin:", error);
      throw error;
    }
  },

  /**
   * Get printers by type (BILL or KOT)
   */
  getByType: async (printerType) => {
    try {
      const response = await apiClient.get(ENDPOINTS.PRINTERS.GET_BY_TYPE(printerType));
      return response?.data || [];
    } catch (error) {
      console.error(`Failed to fetch ${printerType} printers:`, error);
      throw error;
    }
  },

  /**
   * Get default printer for a type
   */
  getDefault: async (printerType) => {
    try {
      const response = await apiClient.get(ENDPOINTS.PRINTERS.GET_DEFAULT(printerType));
      return response?.data || null;
    } catch (error) {
      console.error(`Failed to fetch default ${printerType} printer:`, error);
      throw error;
    }
  },

  /**
   * Get printer by kitchen station
   */
  getByStation: async (stationId) => {
    try {
      const response = await apiClient.get(ENDPOINTS.PRINTERS.GET_BY_STATION(stationId));
      return response?.data || null;
    } catch (error) {
      console.error(`Failed to fetch printer for station ${stationId}:`, error);
      throw error;
    }
  },

  /**
   * Create a new printer configuration
   */
  create: async (data) => {
    try {
      const response = await apiClient.post(ENDPOINTS.PRINTERS.CREATE, data);
      return response?.data || null;
    } catch (error) {
      console.error("Failed to create printer:", error);
      throw error;
    }
  },

  /**
   * Update a printer configuration
   */
  update: async (id, data) => {
    try {
      const response = await apiClient.put(ENDPOINTS.PRINTERS.UPDATE(id), data);
      return response?.data || null;
    } catch (error) {
      console.error(`Failed to update printer ${id}:`, error);
      throw error;
    }
  },

  /**
   * Delete a printer configuration
   */
  delete: async (id) => {
    try {
      const response = await apiClient.delete(ENDPOINTS.PRINTERS.DELETE(id));
      return response?.data || null;
    } catch (error) {
      console.error(`Failed to delete printer ${id}:`, error);
      throw error;
    }
  },

  /**
   * Activate a printer
   */
  activate: async (id) => {
    try {
      const response = await apiClient.put(ENDPOINTS.PRINTERS.ACTIVATE(id));
      return response?.data || null;
    } catch (error) {
      console.error(`Failed to activate printer ${id}:`, error);
      throw error;
    }
  },

  /**
   * Deactivate a printer
   */
  deactivate: async (id) => {
    try {
      const response = await apiClient.put(ENDPOINTS.PRINTERS.DEACTIVATE(id));
      return response?.data || null;
    } catch (error) {
      console.error(`Failed to deactivate printer ${id}:`, error);
      throw error;
    }
  },

  /**
   * Set printer as default for its type
   */
  setDefault: async (id) => {
    try {
      const response = await apiClient.put(ENDPOINTS.PRINTERS.SET_DEFAULT(id));
      return response?.data || null;
    } catch (error) {
      console.error(`Failed to set printer ${id} as default:`, error);
      throw error;
    }
  },

  /**
   * Get list of system printers available on the server
   */
  getSystemPrinters: async () => {
    try {
      const response = await apiClient.get(ENDPOINTS.PRINTERS.SYSTEM_PRINTERS);
      return response?.data || [];
    } catch (error) {
      console.error("Failed to fetch system printers:", error);
      throw error;
    }
  },
};

export default printerService;
